'use client';

import { Box, Typography, ButtonBase, CircularProgress } from '@mui/material';
import { useDarkColors } from '@/hooks/useDarkColors';
import { TEMP_TEXT_MEDIUM_M } from '@/constants/tempTypo';

interface TypeItem {
  key: string;
  label: string;
}

interface TypeSelectDropdownProps {
  open: boolean;
  isLoading: boolean;
  types: TypeItem[];
  selectedKey: string;
  onSelect: (key: string) => void;
}

export default function TypeSelectDropdown({
  open,
  isLoading,
  types,
  selectedKey,
  onSelect,
}: TypeSelectDropdownProps) {
  const c = useDarkColors();

  return (
    <Box
      sx={{
        position: 'absolute',
        top: '100%',
        left: 0,
        right: 0,
        zIndex: 10,
        border: `1px solid ${c.border}`,
        borderRadius: '12px',
        bgcolor: c.bg,
        overflow: 'hidden',
        maxHeight: open ? 300 : 0,
        opacity: open ? 1 : 0,
        transition: 'max-height 0.25s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.2s ease',
        boxShadow: open ? (c.isDark ? '0px 4px 12px rgba(0, 0, 0, 0.3)' : '0px 4px 12px rgba(0, 0, 0, 0.08)') : 'none',
        pointerEvents: open ? 'auto' : 'none',
      }}
    >
      <Box
        sx={{
          maxHeight: 300,
          overflowY: 'scroll',
          WebkitOverflowScrolling: 'touch',
          '&::-webkit-scrollbar': {
            width: '4px',
            display: 'block',
          },
          '&::-webkit-scrollbar-track': {
            bgcolor: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            bgcolor: c.isDark ? '#555555' : '#d9d9d9',
            borderRadius: '4px',
          },
          scrollbarWidth: 'thin',
          scrollbarColor: `${c.isDark ? '#555555' : '#d9d9d9'} transparent`,
        }}
      >
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: '16px' }}>
            <CircularProgress size={20} />
          </Box>
        ) : (
          types.map((type) => (
            <ButtonBase
              key={type.key}
              onClick={() => onSelect(type.key)}
              sx={{
                width: '100%',
                px: '16px',
                py: '14px',
                display: 'flex',
                justifyContent: 'flex-start',
                bgcolor: selectedKey === type.key ? c.bgNotice : c.bg,
              }}
            >
              <Typography
                sx={{
                  ...TEMP_TEXT_MEDIUM_M,
                  color: selectedKey === type.key ? c.primary : c.fg,
                }}
              >
                {type.label}
              </Typography>
            </ButtonBase>
          ))
        )}
      </Box>
    </Box>
  );
}
